/**
 * Enhanced Lazy Loading with Prefetch Support
 * 
 * Kullanıcı deneyimini iyileştirmek için:
 * - Chunk preloading (hover/focus'ta)
 * - Loading state yönetimi
 * - Error boundary entegrasyonu
 * - Retry mekanizması
 */

import React, { lazy, Suspense, useState, useEffect } from 'react';
import { PageLoader } from '../components/common/LoadingSpinner';

/**
 * Prefetch destekli lazy import
 * @param {Function} importFn - Dynamic import function
 * @param {string} chunkName - Chunk adı (opsiyonel, debugging için)
 */
export function lazyWithPrefetch(importFn, chunkName = '') {
    const Component = lazy(importFn);

    // Prefetch fonksiyonu
    Component.prefetch = () => {
        importFn().catch(err => {
            console.warn(`[LazyLoad] Prefetch failed for ${chunkName}:`, err);
        });
    };

    return Component;
}

/**
 * Retry mekanizmalı lazy import
 * Network hataları için 3 deneme yapar
 */
export function lazyWithRetry(importFn, retries = 3, delay = 1000) {
    return lazy(() => {
        const attempt = (retriesLeft) => {
            return importFn().catch(error => {
                if (retriesLeft <= 0) {
                    throw error;
                }
                return new Promise(resolve => {
                    setTimeout(() => resolve(attempt(retriesLeft - 1)), delay);
                });
            });
        };
        return attempt(retries);
    });
}

/**
 * Preload edilecek route'ları tanımla
 * Priority'ye göre sıralı
 */
export const preloadRoutes = {
    high: [
        () => import('../pages/admin/DashboardHome'),
        () => import('../pages/admin/OrdersDashboard'),
    ],
    medium: [
        () => import('../pages/admin/ProductCatalog'),
        () => import('../pages/ProductPool'),
        () => import('../pages/QuestionsDashboard'),
    ],
    low: [
        () => import('../pages/admin/MarketplaceSettings'),
        () => import('../pages/admin/MetricsDashboard'),
        () => import('../pages/AccountPage'),
    ]
};

/**
 * Idle time'da route'ları preload et
 */
export function preloadOnIdle() {
    if ('requestIdleCallback' in window) {
        // High priority routes - hemen
        window.requestIdleCallback(() => {
            preloadRoutes.high.forEach(load => load().catch(() => { }));
        }, { timeout: 2000 });

        // Medium priority - biraz bekle
        window.requestIdleCallback(() => {
            preloadRoutes.medium.forEach(load => load().catch(() => { }));
        }, { timeout: 5000 });

        // Low priority - en son
        window.requestIdleCallback(() => {
            preloadRoutes.low.forEach(load => load().catch(() => { }));
        }, { timeout: 10000 });
    } else {
        // Fallback: setTimeout ile
        setTimeout(() => {
            preloadRoutes.high.forEach(load => load().catch(() => { }));
        }, 1000);
    }
}

/**
 * Link hover'da preload et
 */
export function usePreloadOnHover(importFn) {
    const [preloaded, setPreloaded] = useState(false);

    const onMouseEnter = () => {
        if (!preloaded) {
            importFn().catch(() => { });
            setPreloaded(true);
        }
    };

    return { onMouseEnter };
}

/**
 * Suspense wrapper with fallback
 */
export function LazyRoute({ component: Component, fallback, ...props }) {
    const LoadingFallback = fallback || <PageLoader message="Sayfa yükleniyor..." />;

    return (
        <Suspense fallback={LoadingFallback}>
            <Component {...props} />
        </Suspense>
    );
}

/**
 * Chunk load error handler
 */
export class ChunkLoadErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        // ChunkLoadError'u yakala
        if (error.name === 'ChunkLoadError' ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Failed to fetch dynamically imported')) {
            return { hasError: true, error };
        }
        throw error;
    }

    handleRetry = () => {
        // Sayfayı yenile (chunk'ları temizler)
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="chunk-load-error" style={{
                    padding: '40px',
                    textAlign: 'center',
                    maxWidth: '400px',
                    margin: '100px auto'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#f59e0b', marginBottom: '20px' }} />
                    <h2 style={{ marginBottom: '15px' }}>Sayfa Yüklenemedi</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        Sayfa yüklenirken bir hata oluştu. Bu genellikle ağ bağlantısı sorunu veya uygulama güncellemesi nedeniyle olur.
                    </p>
                    <button
                        onClick={this.handleRetry}
                        style={{
                            padding: '12px 24px',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        <i className="fas fa-sync-alt" style={{ marginRight: '8px' }} />
                        Tekrar Dene
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default {
    lazyWithPrefetch,
    lazyWithRetry,
    preloadOnIdle,
    usePreloadOnHover,
    LazyRoute,
    ChunkLoadErrorBoundary
};
